import React, { ChangeEvent, useEffect } from 'react';
import { useState } from 'react';
import { Box, Button, HStack, Text } from '@chakra-ui/react';
import {
  AudiobooksService,
  BooksService,
  CompleteAudioBookReturn,
} from '../../../generated/client';
import { CompleteBookReturn } from '../../../generated/client/models/CompleteBookReturn';
import { isCompleteAudioBookReturn, isCompleteBookReturn } from '../../../types/TypeUtils';
import { BsPersonCircle } from 'react-icons/bs';

/**
 * The CommentSection does the following:
 * -- displays comments on a particular book or audiobook
 * -- displays textbox and submit button for user input
 *
 * @param props: A 'completeReturn', which is a CompleteBookReturn, CompleteAudioBookReturn, or undefined
 *             : A 'service', which is a BookService or AudiobooksService
 */

interface Props {
  completeReturn: CompleteBookReturn | CompleteAudioBookReturn | undefined;
  service: BooksService | AudiobooksService;
}

export function CommentSection({ completeReturn, service }: Props): JSX.Element {
  const [updatedComments, setUpdatedComments] = useState('');
  const [commentList, setCommentList] = useState<string[]>([]);
  const [completeUpdatedReturn, setCompleteUpdatedReturn] = useState<
    CompleteBookReturn | CompleteAudioBookReturn
  >();

  // provides updates for comments submitted
  const handleCommentUpdate = (event: ChangeEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    setUpdatedComments(event.target.value);
  };

  // handles the submission of comments
  const handleCommentSubmit = async () => {
    if (completeUpdatedReturn) {
      const comments = completeUpdatedReturn.comments ?? [];
      comments.push(updatedComments);
      if (isCompleteBookReturn(completeUpdatedReturn)) {
        const dataToSend: CompleteBookReturn = {
          id: completeUpdatedReturn.id,
          comments: comments,
          ratings: completeUpdatedReturn.ratings ?? [],
          title: completeUpdatedReturn.title,
          authors: completeUpdatedReturn.authors,
          content: completeUpdatedReturn.content,
        };
        setCompleteUpdatedReturn(dataToSend);
        const castService = service as BooksService;
        await castService.updateBook(dataToSend);
      }
      if (isCompleteAudioBookReturn(completeUpdatedReturn)) {
        const dataToSend: CompleteAudioBookReturn = {
          id: completeUpdatedReturn.id,
          comments: comments,
          ratings: completeUpdatedReturn.ratings ?? [],
          title: completeUpdatedReturn.title,
          author: completeUpdatedReturn.author,
          content: completeUpdatedReturn.content,
        };
        setCompleteUpdatedReturn(dataToSend);
        const castService = service as AudiobooksService;
        await castService.updateAudioBook(dataToSend);
      }
    }
    setUpdatedComments('');
  };

  useEffect(() => {
    (async () => {
      if (completeReturn) {
        if (isCompleteBookReturn(completeReturn)) {
          const castService = service as BooksService;
          const getUpdatedReturn = await castService.getBook(completeReturn.id);
          setCompleteUpdatedReturn(getUpdatedReturn);
        }
        if (isCompleteAudioBookReturn(completeReturn)) {
          const castService = service as AudiobooksService;
          const getUpdatedReturn = await castService.getAudioBook(completeReturn.id);
          setCompleteUpdatedReturn(getUpdatedReturn);
        }
        if (completeUpdatedReturn && completeUpdatedReturn.comments) {
          setCommentList(completeUpdatedReturn.comments);
        }
      }
    })();
  }, [completeReturn, completeUpdatedReturn, service]);

  return (
    <div>
      <Box>
        {/* adds the comment list */}
        {commentList.map((comment, id) => (
          <div key={id} className='wrapper'>
            <HStack maxW='36rem'>
              <BsPersonCircle />
              <Text maxW='30rem'> {comment}</Text>
            </HStack>
            <hr></hr>
          </div>
        ))}

        <div>
          <p>
            <b>Comment</b>
          </p>
          <textarea
            style={{
              height: 50,
              width: 400,
              outlineStyle: 'solid',
              outlineColor: 'gray',
              outlineWidth: '1px',
              padding: 10,
            }}
            value={updatedComments}
            onChange={handleCommentUpdate}
          />
          <br />
          {/* takes care of the submissions for comments */}
          <Button
            style={{ width: 400, marginBottom: 10 }}
            colorScheme='teal'
            onClick={handleCommentSubmit}>
            Save
          </Button>
        </div>
      </Box>
    </div>
  );
}
